import { Component, computed, inject, Signal } from '@angular/core';
import { BackgroundColorDirective, ButtonDirective, ColorDirective, TypographyDirective } from '@dartcounter/ui';
import { SmartDeviceApiService } from '@dc-api/smart-device.api.service';
import { SmartDeviceFirmware, User } from '@dc-core/dc-backend/dc-classes';
import { VersionHelper } from '@dc-core/dc-helpers/version.helper';
import { AlertPayload, AlertService } from '@dc-core/dc-services/alert.service';
import { IonMenu, NavController } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { UserStateService } from 'src/app/modules/user/services/user-state.service';
import { OldIconComponent } from 'src/app/shared/components/icon/icon.component';
import { OptionButtonComponent } from 'src/app/shared/components/option-button/option-button.component';
import { PromptDialogComponent } from 'src/app/shared/dialogs/prompt/prompt.dialog';
import { SearchPlayerDialogComponent } from 'src/app/shared/dialogs/search-player/search-player.dialog';
import { HideScrollbarDirective } from 'src/app/shared/directives/hide-scrollbar.directive';
import { ModalService } from 'src/app/shared/services/modal.service';
import { centerDialogClasses, slideDialogClasses } from 'src/app/shared/utils/dialog.utils';
import { environment } from 'src/environments/environment';

import { FirmwareUpdateMode } from '../../../dialogs/smart-device-firmware-update/smart-device-firmware-update.dialog';
import {
    SmartDevicesChangeFirmwareComponent,
    SmartDevicesChangeFirmwarePayload,
} from '../../../dialogs/smart-devices-change-firmware/smart-devices-change-firmware.component';
import {
    VirtFirmwareUpdateDialogComponent,
    VirtFirmwareUpdateDialogPayload,
} from '../../../dialogs/virt-firmware-update/virt-firmware-update.dialog';
import { ConnectionStatus, DartCounterSmartDevice, VirtFirmwareSettings } from '../../../models/smart-devices.models';
import { VirtStateService } from '../../../omni/services/virt-state.service';
import { SmartDeviceService } from '../../../services/smart-device.service';
import { SmartDevicesStateService } from '../../../services/smart-devices-state.service';
// import { OmniStateService } from '../../services/omni-state.service';

@Component({
    selector: 'app-virt-settings-menu',
    standalone: true,
    imports: [
        IonMenu,
        BackgroundColorDirective,
        ColorDirective,
        TypographyDirective,
        OptionButtonComponent,
        TranslateModule,
        OldIconComponent,
        ButtonDirective,
        HideScrollbarDirective,
    ],
    templateUrl: './virt-settings-menu.component.html',
    styleUrl: './virt-settings-menu.component.css',
})
export class VirtSettingsMenuComponent {
    public readonly isProduction = environment.production;
    public readonly isWeb = environment.isWeb;
    public readonly ConnectionStatus = ConnectionStatus;

    public authService: AuthService = inject(AuthService);

    private userStateService: UserStateService = inject(UserStateService);
    private modalService: ModalService = inject(ModalService);
    private smartDeviceApiService: SmartDeviceApiService = inject(SmartDeviceApiService);
    private smartDevicesStateService: SmartDevicesStateService = inject(SmartDevicesStateService);
    private alertService: AlertService = inject(AlertService);
    private navController: NavController = inject(NavController);
    private virtStateService: VirtStateService = inject(VirtStateService);
    private smartDeviceService: SmartDeviceService = inject(SmartDeviceService);

    public firmwareSettings: Signal<VirtFirmwareSettings> = computed(() =>
        this.virtStateService.currentFirmwareSettings()
    );

    public selectedVirt: Signal<DartCounterSmartDevice> = computed(() => this.virtStateService.currentSmartDevice());

    public isAdmin: Signal<boolean> = computed(() =>
        this.smartDevicesStateService.isAdmin(this.virtStateService.currentSmartDeviceNetwork(), this.selectedVirt())
    );

    public isDev: Signal<boolean> = computed(
        () => !!this.userStateService.currentGroups().find((group) => group.name === 'omni_dev')
    );

    public versionHelper = VersionHelper;

    public shareVirt(): void {
        this.modalService
            .openModal<any, User>({
                component: SearchPlayerDialogComponent,
                componentProps: {},
                cssClass: slideDialogClasses,
                backdropDismiss: true,
            })
            .then((user) => {
                if (user) {
                    this.smartDeviceApiService
                        .inviteUserToSmartDeviceNetwork({
                            smartDeviceNetworkId: this.selectedVirt().network_id,
                            user_id: user.id,
                            device_id: this.selectedVirt().id,
                            has_full_access: this.selectedVirt() ? false : true,
                            is_admin: false,
                            expires_at: null,
                        })
                        .then(() => {
                            this.alertService.createAlert({
                                title: $localize`:@@PLAYER_INVITED:Player invited`,
                                icon: 'success',
                            });
                        })
                        .catch((err) => this.alertService.errorFromApi(err));
                }
            });
    }

    public repairVirt(): void {
        this.modalService.openModal<VirtFirmwareUpdateDialogPayload>({
            component: VirtFirmwareUpdateDialogComponent,
            componentProps: {
                updateMode: FirmwareUpdateMode.REPAIR,
            },
            cssClass: slideDialogClasses,
            backdropDismiss: false,
        });
    }

    public rebootDevice(): void {
        this.modalService
            .openModal<any, User>({
                component: PromptDialogComponent,
                componentProps: {
                    title: $localize`:@@REBOOT_DEVICE:Reboot device`,
                    text: $localize`:@@REBOOTING_DEVICE:Do you want to reboot this device?`,
                    cancelText: $localize`:@@CANCEL:Cancel`,
                    confirmText: $localize`:@@CONFIRM:Confirm`,
                    confirmColor: 'red',
                } as AlertPayload,
                cssClass: centerDialogClasses,
                showBackdrop: true,
                backdropDismiss: false,
            })
            .then((dialogRes) => {
                if (dialogRes) {
                    this.smartDeviceService.reboot(this.selectedVirt().ip_address);
                }
            });
    }

    public removeDevice(): void {
        this.modalService
            .openModal<any, User>({
                component: PromptDialogComponent,
                componentProps: {
                    title: $localize`:@@DELETE_DEVICE:Remove device`,
                    text: $localize`:@@DELETING_DEVICE:Do you want to remove this device?`,
                    cancelText: $localize`:@@CANCEL:Cancel`,
                    confirmText: $localize`:@@CONFIRM:Confirm`,
                    confirmColor: 'red',
                } as AlertPayload,
                cssClass: centerDialogClasses,
                showBackdrop: true,
                backdropDismiss: false,
            })
            .then((dialogRes) => {
                if (dialogRes) {
                    this.smartDeviceApiService
                        .deleteSmartDeviceById({ smartDeviceId: this.selectedVirt().id })
                        .then(() => {
                            this.smartDevicesStateService.removeDeviceFromRefreshList(this.selectedVirt().id);
                            this.smartDevicesStateService.removeDeviceFromNetwork(
                                this.selectedVirt().network_id,
                                this.selectedVirt().id
                            );
                            this.virtStateService.clearCurrentSmartDevice();
                            this.navController.back();
                        });
                }
            });
    }

    public changeFirmware(): void {
        this.modalService
            .openModal<SmartDevicesChangeFirmwarePayload, SmartDeviceFirmware>({
                component: SmartDevicesChangeFirmwareComponent,
                componentProps: {
                    smartDeviceType: 'virt_cam',
                },
                cssClass: centerDialogClasses,
                showBackdrop: true,
                backdropDismiss: false,
            })
            .then((firmware) => {
                if (firmware) {
                    this.modalService.openModal<VirtFirmwareUpdateDialogPayload>({
                        component: VirtFirmwareUpdateDialogComponent,
                        componentProps: {
                            updateMode: FirmwareUpdateMode.FORCE,
                            firmware: firmware,
                        },
                        cssClass: slideDialogClasses,
                        backdropDismiss: true,
                    });
                }
            });
    }
}
